/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Link } from 'react-router-dom';
import Fuse1 from 'fuse.js';
import * as Fuse2 from 'fuse.js';
import { Text, TextInput, BrandColors } from '@blend/enterprise-components';
import { BlendBridge } from 'blb-types/blendBridge';
import { removeDefaultAnchorStyling } from 'blb-styles/mixins';

// Reconcile Webpack and unit test bundling
const Fuse = Fuse1 || Fuse2;

export namespace PipelineSearch {
  export interface Props {
    loans: BlendBridge.API.Loan[];
  }
}

const styles = StyleSheet.create({
  result: {
    display: 'block',
    minHeight: 100,
    padding: '16px 24px',
    ...removeDefaultAnchorStyling(),

    ':focus': {
      backgroundColor: BrandColors.CLOUD_30,
    },
    ':hover': {
      backgroundColor: BrandColors.CLOUD_30,
    },
  },
  resultBorder: {
    borderBottom: `1px solid ${BrandColors.CLOUD_30}`,
  },
  results: {
    backgroundColor: BrandColors.WHITE,
    border: `1px solid ${BrandColors.CLOUD_50}`,
    boxShadow: `0 2px 2px 1px ${BrandColors.CLOUD_50}`,
    maxHeight: 662,
    overflowY: 'auto',
    position: 'absolute',
    width: '100%',
    zIndex: 1,
  },
  root: {
    position: 'relative',
  },
});

const useFuse = <T,>(list: T[], searchTerm: string, fuseOptions?: Fuse1.IFuseOptions<T>) => {
  const fuse = React.useMemo(() => {
    return new Fuse(list, fuseOptions);
  }, [list, fuseOptions]);

  const results = React.useMemo(() => {
    return fuse.search(searchTerm);
  }, [fuse, searchTerm]);

  return results;
};

export const PipelineSearch: React.FC<PipelineSearch.Props> = ({ loans }) => {
  const [query, setQuery] = React.useState<string>('');
  const [inputFocused, setInputFocused] = React.useState<boolean>(true);
  const results = useFuse(loans, query, {
    keys: ['loanRefNumber', 'primaryBorrowerName', 'propertyAddress.lineText'],
    threshold: 0.2,
  });

  return (
    <div
      className={css(styles.root)}
      id="pipeline-search"
      onBlur={e => {
        const currentTarget = e.currentTarget;
        const nextTarget = e.relatedTarget;

        if (nextTarget instanceof Element && currentTarget.contains(nextTarget)) return;
        setInputFocused(false);
      }}
      onFocus={() => setInputFocused(true)}
    >
      <TextInput
        configOverrides={{
          icon: {
            position: 'left',
            size: 16,
            type: 'search',
          },
        }}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search for loans"
        type="search"
        value={query}
      />
      {results.length > 0 && inputFocused && (
        <ul className={css(styles.results)} id="pipeline-search-results">
          {results.slice(0, 10).map(({ item }, index) => (
            <li key={item.loanId}>
              <Link className={css(styles.result, index < 10 && styles.resultBorder)} to={`${item.loanId}/dashboard`}>
                <div>
                  <Text weight="semibold">{item.primaryBorrowerName}</Text>
                </div>
                <div>
                  <Text fontSize="sm">{item.loanRefNumber}</Text>
                </div>
                {item.propertyAddress && (
                  <div>
                    <Text fontSize="sm">
                      {item.propertyAddress.lineText}, {item.propertyAddress.cityName}, {item.propertyAddress.stateCode}{' '}
                      {item.propertyAddress.zipCode}
                    </Text>
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
